import { dynamicPage } from '@metorial/dynamic-component';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';

// Workforce (portal admin) routes, cloud-spec §5. Registered once in
// product/index.tsx (productWorkforceSlice). Page files belong to Wave 1:
//   _overview/, settings/  → WS-A     access/ → WS-B     identity/ → WS-C
// Replace a page's body, keep its export name; ask the orchestrator before
// adding routes here.

type PageComponent = (...p: any[]) => ReactNode;

let page = <T extends Record<string, any>>(load: () => Promise<T>, name: keyof T) =>
  dynamicPage<any[]>(() => load().then(m => m[name] as PageComponent));

let WorkforceRedirectPage = page(() => import('./redirect'), 'WorkforceRedirectPage');
let PortalLayout = page(() => import('./_layout'), 'PortalLayout');

let PortalOverviewPage = page(() => import('./_overview'), 'PortalOverviewPage');

// Access
let PortalGroupsPage = page(() => import('./access/groups'), 'PortalGroupsPage');
let PortalGroupLayout = page(() => import('./access/group/_layout'), 'PortalGroupLayout');
let PortalGroupOverviewPage = page(
  () => import('./access/group/index'),
  'PortalGroupOverviewPage'
);
let PortalGroupMembersPage = page(
  () => import('./access/group/members'),
  'PortalGroupMembersPage'
);
let PortalGroupAccessPage = page(
  () => import('./access/group/access'),
  'PortalGroupAccessPage'
);
let PortalGroupMarketplaceManagersPage = page(
  () => import('./access/group/marketplaceManagers'),
  'PortalGroupMarketplaceManagersPage'
);
let PortalGroupSettingsPage = page(
  () => import('./access/group/settings'),
  'PortalGroupSettingsPage'
);
let PortalSkillsPage = page(() => import('./access/skills'), 'PortalSkillsPage');
let PortalSkillMarketplacesPage = page(
  () => import('./access/skillMarketplaces'),
  'PortalSkillMarketplacesPage'
);
let PortalIntegrationsPage = page(
  () => import('./access/integrations'),
  'PortalIntegrationsPage'
);
let PortalAccessListingPage = page(
  () => import('./access/accessListing'),
  'PortalAccessListingPage'
);
let PortalAccessRequestsPage = page(
  () => import('./access/accessRequests'),
  'PortalAccessRequestsPage'
);

// Identity
let PortalAccountsPage = page(() => import('./identity/accounts'), 'PortalAccountsPage');
let PortalInvitesPage = page(() => import('./identity/invites'), 'PortalInvitesPage');
let PortalAccountLayout = page(
  () => import('./identity/account/_layout'),
  'PortalAccountLayout'
);
let PortalAccountOverviewPage = page(
  () => import('./identity/account/index'),
  'PortalAccountOverviewPage'
);
let PortalAccountAccessPage = page(
  () => import('./identity/account/access'),
  'PortalAccountAccessPage'
);
let PortalAccountAgentsPage = page(
  () => import('./identity/account/agents'),
  'PortalAccountAgentsPage'
);
let PortalAccountOperationsPage = page(
  () => import('./identity/account/operations'),
  'PortalAccountOperationsPage'
);
let PortalAccountConnectionsPage = page(
  () => import('./identity/account/connections'),
  'PortalAccountConnectionsPage'
);
let PortalAccountDelegationsPage = page(
  () => import('./identity/account/delegations'),
  'PortalAccountDelegationsPage'
);
let PortalAccountMagicMcpServersPage = page(
  () => import('./identity/account/magicMcpServers'),
  'PortalAccountMagicMcpServersPage'
);
let PortalAccountSettingsPage = page(
  () => import('./identity/account/settings'),
  'PortalAccountSettingsPage'
);
let PortalAgentsPage = page(() => import('./identity/agents'), 'PortalAgentsPage');
let PortalIdentitiesPage = page(() => import('./identity/identities'), 'PortalIdentitiesPage');
let PortalIdentityLayout = page(
  () => import('./identity/identity/_layout'),
  'PortalIdentityLayout'
);
let PortalIdentityOverviewPage = page(
  () => import('./identity/identity/index'),
  'PortalIdentityOverviewPage'
);
let PortalIdentityDelegationsPage = page(
  () => import('./identity/identity/delegations'),
  'PortalIdentityDelegationsPage'
);
let PortalIdentityDelegationRequestsPage = page(
  () => import('./identity/identity/delegationRequests'),
  'PortalIdentityDelegationRequestsPage'
);
let PortalIdentitySettingsPage = page(
  () => import('./identity/identity/settings'),
  'PortalIdentitySettingsPage'
);
let PortalActorsPage = page(() => import('./identity/actors'), 'PortalActorsPage');

// Settings
let PortalHighlightsPage = page(() => import('./settings/highlights'), 'PortalHighlightsPage');
let PortalSettingsLayout = page(() => import('./settings/_layout'), 'PortalSettingsLayout');
let PortalSettingsGeneralPage = page(
  () => import('./settings/index'),
  'PortalSettingsGeneralPage'
);
let PortalSettingsClientPage = page(
  () => import('./settings/client'),
  'PortalSettingsClientPage'
);
let PortalSettingsSkillsPage = page(
  () => import('./settings/skills'),
  'PortalSettingsSkillsPage'
);

export let workforceRoutes: RouteObject[] = [
  { path: 'workforce', element: <WorkforceRedirectPage /> },
  {
    path: 'portal/:portalId',
    element: <PortalLayout />,
    children: [
      { path: '', element: <PortalOverviewPage /> },

      { path: 'groups', element: <PortalGroupsPage /> },
      {
        path: 'group/:groupId',
        element: <PortalGroupLayout />,
        children: [
          { path: '', element: <PortalGroupOverviewPage /> },
          { path: 'members', element: <PortalGroupMembersPage /> },
          { path: 'access', element: <PortalGroupAccessPage /> },
          { path: 'marketplace-managers', element: <PortalGroupMarketplaceManagersPage /> },
          { path: 'settings', element: <PortalGroupSettingsPage /> }
        ]
      },
      { path: 'resources/skills', element: <PortalSkillsPage /> },
      { path: 'resources/skills/marketplaces', element: <PortalSkillMarketplacesPage /> },
      { path: 'resources/integrations', element: <PortalIntegrationsPage /> },
      { path: 'resources/highlights', element: <PortalHighlightsPage /> },
      { path: 'access-listing/:listingId', element: <PortalAccessListingPage /> },
      { path: 'access-requests', element: <PortalAccessRequestsPage /> },

      { path: 'accounts', element: <PortalAccountsPage /> },
      { path: 'accounts/invites', element: <PortalInvitesPage /> },
      {
        path: 'account/:accountId',
        element: <PortalAccountLayout />,
        children: [
          { path: '', element: <PortalAccountOverviewPage /> },
          { path: 'access', element: <PortalAccountAccessPage /> },
          { path: 'agents', element: <PortalAccountAgentsPage /> },
          { path: 'operations', element: <PortalAccountOperationsPage /> },
          { path: 'connections', element: <PortalAccountConnectionsPage /> },
          { path: 'delegations', element: <PortalAccountDelegationsPage /> },
          { path: 'magic-mcp-servers', element: <PortalAccountMagicMcpServersPage /> },
          { path: 'settings', element: <PortalAccountSettingsPage /> }
        ]
      },
      { path: 'agents', element: <PortalAgentsPage /> },
      { path: 'identities', element: <PortalIdentitiesPage /> },
      {
        path: 'identity/:identityId',
        element: <PortalIdentityLayout />,
        children: [
          { path: '', element: <PortalIdentityOverviewPage /> },
          { path: 'delegations', element: <PortalIdentityDelegationsPage /> },
          { path: 'delegation-requests', element: <PortalIdentityDelegationRequestsPage /> },
          { path: 'settings', element: <PortalIdentitySettingsPage /> }
        ]
      },
      { path: 'actors', element: <PortalActorsPage /> },

      {
        path: 'settings',
        element: <PortalSettingsLayout />,
        children: [
          { path: '', element: <PortalSettingsGeneralPage /> },
          { path: 'client', element: <PortalSettingsClientPage /> },
          { path: 'skills', element: <PortalSettingsSkillsPage /> }
        ]
      }
    ]
  }
];
